import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './hooks/use-auth'
import { ThemeProvider } from './components/ThemeProvider'
import { PreferencesProvider } from './hooks/use-preferences'
import { ProtectedRoute } from './components/ProtectedRoute'
import { useFeatures } from './hooks/use-features'
import { RoleGuard } from './components/RoleGuard'
import { Layout } from './components/Layout'
import { LoginPage } from './pages/Login'
import { DashboardPage } from './pages/Dashboard'
import { CentralHomePage } from './pages/CentralHomePage'
import { PatientListPage } from './pages/patients/PatientListPage'
import { PatientEditPage } from './pages/patients/PatientEditPage'
import { ResultListPage } from './pages/results/ResultListPage'
import { ResultDetailPage } from './pages/results/ResultDetailPage'
import { DeviceListPage } from './pages/devices/DeviceListPage'
import { DeviceEditPage } from './pages/devices/DeviceEditPage'
import { InstrumentErrorsPage } from './pages/devices/InstrumentErrorsPage'
import { UserListPage } from './pages/users/UserListPage'
import { UserEditPage } from './pages/users/UserEditPage'
import { OrderListPage } from './pages/orders/OrderListPage'
import { OrderEditPage } from './pages/orders/OrderEditPage'
import { EhrListPage } from './pages/ehr/EhrListPage'
import { EhrConfigPage } from './pages/ehr/EhrConfigPage'
import { TestTypeListPage } from './pages/admin/TestTypeListPage'
import { TestTypeEditPage } from './pages/admin/TestTypeEditPage'
import { ProviderListPage } from './pages/admin/ProviderListPage'
import { ProviderEditPage } from './pages/admin/ProviderEditPage'
import { QcLotListPage } from './pages/qc/QcLotListPage'
import { QcLotEditPage } from './pages/qc/QcLotEditPage'
import { QcLotDetailPage } from './pages/qc/QcLotDetailPage'
import { OrgSettingsPage } from './pages/admin/OrgSettingsPage'
import { SystemStatusPage } from './pages/system/SystemStatusPage'
import { SettingsPage } from './pages/settings/SettingsPage'
import { LabClientListPage } from './pages/settings/LabClientListPage'
import { PanelListPage } from './pages/settings/PanelListPage'
import { PanelDictionaryListPage } from './pages/settings/PanelDictionaryListPage'
import { PayerListPage } from './pages/settings/PayerListPage'
import { DiagnosisCodeListPage } from './pages/settings/DiagnosisCodeListPage'
import { ProcedureCodeListPage } from './pages/settings/ProcedureCodeListPage'
import { ReferenceLabListPage } from './pages/settings/ReferenceLabListPage'
import { MediaSettingsPage } from './pages/settings/MediaSettingsPage'
import { LogRetentionPanel } from './pages/settings/LogRetentionPanel'
import { UpdatesPanel } from './pages/settings/UpdatesPanel'
import { ClinicalTrialListPage } from './pages/settings/ClinicalTrialListPage'
import { DemoPage } from './pages/settings/DemoPage'
import { MigrationStatusPage } from './pages/settings/MigrationStatusPage'
import { PrinterListPage } from './pages/settings/PrinterListPage'
import { LabelTemplateListPage } from './pages/settings/LabelTemplateListPage'
import { CdphSettingsPage } from './pages/settings/CdphSettingsPage'
import { CdphReportsPage } from './pages/settings/CdphReportsPage'
import { PreferencesPage } from './pages/PreferencesPage'
import { PreferencesNotificationsPage } from './pages/PreferencesNotificationsPage'
import { LicensePage } from './pages/settings/LicensePage'
import { QcConfigurationsPage } from './pages/qc/QcConfigurationsPage'
import { QcProgramsPage } from './pages/qc/QcProgramsPage'
import { QcLeveyJenningsPage } from './pages/qc/QcLeveyJenningsPage'
import { OnboardingPage } from './pages/onboarding/OnboardingPage'
import { LicenseListPage } from './pages/licenses/LicenseListPage'
import { NewEnvironmentPage } from './pages/licenses/NewEnvironmentPage'
import { LicenseDetailPage } from './pages/licenses/LicenseDetailPage'
import { FleetListPage } from './pages/fleet/FleetListPage'
import { UdmRolloutsPage } from './pages/fleet/UdmRolloutsPage'
import { FleetCustomersPage } from './pages/fleet/FleetCustomersPage'
import { FleetCustomerDetailPage } from './pages/fleet/FleetCustomerDetailPage'
import { MyOrganizationPage } from './pages/account/MyOrganizationPage'
import { MyProfilePage } from './pages/account/MyProfilePage'
import { FleetAlertsPage } from './pages/fleet/FleetAlertsPage'
import { FleetDevicesPage } from './pages/fleet/FleetDevicesPage'
import { AccessioningPage } from './pages/workflows/AccessioningPage'
import { TestingPage } from './pages/workflows/TestingPage'
import { QcReviewPage } from './pages/qc/QcReviewPage'
import { EhrOnboardingWizard } from './pages/ehr/EhrOnboardingWizard'
import { AdminLogsPage } from './pages/admin/AdminLogsPage'
import { OperationalEventsPage } from './pages/admin/OperationalEventsPage'
import { AuditTrailPage } from './pages/admin/AuditTrailPage'
import { MetaAnalyticsPage } from './pages/admin/MetaAnalyticsPage'
import { ReportsPage } from './pages/admin/ReportsPage'
import { RemoteAccessPage } from './pages/admin/RemoteAccessPage'
import { ContainerStatusPage } from './pages/admin/ContainerStatusPage'
import { ContainerShellPage } from './pages/admin/ContainerShellPage'
import { ScheduledJobsPage } from './pages/admin/ScheduledJobsPage'
import { FleetEnvironmentDetailPage } from './pages/fleet/FleetEnvironmentDetailPage'
import { FleetTopologyPage } from './pages/fleet/FleetTopologyPage'
import { FleetAuditPage } from './pages/fleet/FleetAuditPage'
import { FleetLogsPage } from './pages/fleet/FleetLogsPage'
import { AgentSuggestionsPage } from './pages/fleet/AgentSuggestionsPage'
import { SupportRequestsPage } from './pages/fleet/SupportRequestsPage'
import { MigrationDashboardPage } from './pages/fleet/MigrationDashboardPage'
import { BackupDashboardPage } from './pages/fleet/BackupDashboardPage'
import { AgentTokensPage } from './pages/admin/AgentTokensPage'
import { ExternalApiKeysPage } from './pages/admin/ExternalApiKeysPage'
import { AnalyticsSharingPage } from './pages/admin/AnalyticsSharingPage'
import { OffsiteBackupPage } from './pages/admin/OffsiteBackupPage'
import { ArchivePage } from './pages/admin/ArchivePage'
import { TenantSecretsPage } from './pages/admin/TenantSecretsPage'
import { EhrDashboardPage } from './pages/admin/EhrDashboardPage'
import { FaxConfigPage } from './pages/admin/FaxConfigPage'
import { FaxImportLogsPage } from './pages/admin/FaxImportLogsPage'
import { SupportSessionsPage } from './pages/fleet/SupportSessionsPage'
import { SupportAccessReportPage } from './pages/admin/SupportAccessReportPage'
import { StaffGrantsPage } from './pages/fleet/StaffGrantsPage'
import { MyActiveEdgesPage } from './pages/fleet/MyActiveEdgesPage'
import { MedflowStaffAccessPage } from './pages/admin/MedflowStaffAccessPage'
import { ProvisionWizardPage } from './pages/fleet/ProvisionWizardPage'
import DeviceProvisioningPage from './pages/fleet/DeviceProvisioningPage'
import { ProvisioningConfigPage } from './pages/admin/ProvisioningConfigPage'
import { RateLimitSettingsPage } from './pages/admin/RateLimitSettingsPage'

function HomeRouter() {
  const features = useFeatures()
  if (features.central) return <CentralHomePage />
  return <DashboardPage />
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <PreferencesProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* Onboarding wizard sits outside the Layout chrome but inside the
              ProtectedRoute guard so we still enforce auth, while not showing
              the sidebar/header until a real admin exists. */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<HomeRouter />} />
                <Route path="/preferences" element={<PreferencesPage />} />
                <Route path="/preferences/notifications" element={<PreferencesNotificationsPage />} />

                {/* Workflow pages */}
                <Route
                  path="/accessioning"
                  element={
                    <RoleGuard roles={['admin', 'tech', 'lab_tech']}>
                      <AccessioningPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/testing"
                  element={
                    <RoleGuard roles={['admin', 'tech', 'lab_tech']}>
                      <TestingPage />
                    </RoleGuard>
                  }
                />

                {/* Customer license portal -- central-hub only, enforced
                server-side (fleet routes only register when MEDFLOW_CENTRAL
                is set) and client-side by RoleGuard. */}
                <Route
                  path="/licenses"
                  element={
                    <RoleGuard roles={['customer_admin', 'medflow_admin']}>
                      <LicenseListPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/licenses/new"
                  element={
                    <RoleGuard roles={['customer_admin', 'medflow_admin']}>
                      <NewEnvironmentPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/licenses/:id"
                  element={
                    <RoleGuard roles={['customer_admin', 'medflow_admin']}>
                      <LicenseDetailPage />
                    </RoleGuard>
                  }
                />

                {/* MedFlow-admin-only fleet view. Shares the LicenseDetailPage
                for drill-in since the detail shape is identical across
                roles (server scoping already enforced). */}
                <Route
                  path="/fleet"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FleetListPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/alerts"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FleetAlertsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/devices"
                  element={
                    <RoleGuard roles={['medflow_admin', 'customer_admin']}>
                      <FleetDevicesPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/customers"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FleetCustomersPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/topology"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FleetTopologyPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/customers/:id"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FleetCustomerDetailPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/my-organization"
                  element={
                    <RoleGuard roles={['customer_admin']}>
                      <MyOrganizationPage />
                    </RoleGuard>
                  }
                />
                <Route path="/my-profile" element={<MyProfilePage />} />
                <Route
                  path="/fleet/audit"
                  element={
                    <RoleGuard roles={['customer_admin', 'medflow_admin']}>
                      <FleetAuditPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/logs"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FleetLogsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/events"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <OperationalEventsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/local-audit"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <AuditTrailPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/agent-suggestions"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <AgentSuggestionsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/support-requests"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <SupportRequestsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/backups"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <BackupDashboardPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/migration"
                  element={
                    <RoleGuard roles={['medflow_admin', 'customer_admin']}>
                      <MigrationDashboardPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/support-sessions"
                  element={
                    <RoleGuard roles={['customer_admin', 'medflow_admin']}>
                      <SupportSessionsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/admin/agent-tokens"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <AgentTokensPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/admin/external-api-keys"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <ExternalApiKeysPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/admin/secrets"
                  element={
                    <RoleGuard roles={['admin', 'customer_admin']}>
                      <TenantSecretsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/:id"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FleetEnvironmentDetailPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/licenses/alerts"
                  element={
                    <RoleGuard roles={['customer_admin', 'medflow_admin']}>
                      <FleetAlertsPage />
                    </RoleGuard>
                  }
                />

                <Route path="/patients" element={<PatientListPage />} />
                <Route path="/patients/:id" element={<PatientEditPage />} />
                <Route path="/results" element={<ResultListPage />} />
                <Route path="/results/:id" element={<ResultDetailPage />} />
                <Route path="/devices" element={<DeviceListPage />} />
                <Route path="/devices/:id" element={<DeviceEditPage />} />
                <Route path="/devices/errors" element={<InstrumentErrorsPage />} />
                <Route path="/orders" element={<OrderListPage />} />
                <Route path="/orders/:id" element={<OrderEditPage />} />

                {/* QC with sub-views */}
                <Route path="/qc" element={<Navigate to="/qc/review" replace />} />
                <Route path="/qc/review" element={<QcReviewPage />} />
                <Route path="/qc/lots" element={<QcLotListPage />} />
                <Route path="/qc/lots/new" element={<QcLotEditPage />} />
                <Route path="/qc/lots/:id" element={<QcLotDetailPage />} />
                <Route path="/qc/configurations" element={<QcConfigurationsPage />} />
                <Route path="/qc/programs" element={<QcProgramsPage />} />
                <Route path="/qc/levey-jennings" element={<QcLeveyJenningsPage />} />

                {/* Settings -- tabbed admin pages */}
                <Route
                  path="/settings"
                  element={
                    <RoleGuard roles={['admin']}>
                      <SettingsPage />
                    </RoleGuard>
                  }
                >
                  <Route index element={<Navigate to="/settings/providers" replace />} />
                  <Route path="providers" element={<ProviderListPage />} />
                  <Route path="providers/:id" element={<ProviderEditPage />} />
                  <Route path="clients" element={<LabClientListPage />} />
                  <Route path="ehr" element={<EhrListPage />} />
                  <Route path="ehr/:id" element={<EhrConfigPage />} />
                  <Route path="payers" element={<PayerListPage />} />
                  <Route path="diagnosis-codes" element={<DiagnosisCodeListPage />} />
                  <Route path="procedure-codes" element={<ProcedureCodeListPage />} />
                  <Route path="panel-management" element={<PanelListPage />} />
                  <Route path="panel-dictionary" element={<PanelDictionaryListPage />} />
                  <Route path="reference-lab" element={<ReferenceLabListPage />} />
                  <Route path="users" element={<UserListPage />} />
                  <Route path="users/:id" element={<UserEditPage />} />
                  <Route path="test-types" element={<TestTypeListPage />} />
                  <Route path="test-types/:id" element={<TestTypeEditPage />} />
                  <Route path="org" element={<OrgSettingsPage />} />
                  <Route path="media" element={<MediaSettingsPage />} />
                  <Route path="logging" element={<LogRetentionPanel />} />
                  <Route path="updates" element={<UpdatesPanel />} />
                  <Route path="clinical-trial" element={<ClinicalTrialListPage />} />
                  <Route path="license" element={<LicensePage />} />
                  <Route path="system" element={<SystemStatusPage />} />
                  <Route path="migration" element={<MigrationStatusPage />} />
                  <Route path="demo" element={<DemoPage />} />
                  <Route path="printers" element={<PrinterListPage />} />
                  <Route path="label-templates" element={<LabelTemplateListPage />} />
                  <Route path="cdph" element={<CdphSettingsPage />} />
                  <Route path="cdph-reports" element={<CdphReportsPage />} />
                </Route>

                {/* EHR onboarding wizard — admin only, standalone page (outside settings tab shell) */}
                <Route
                  path="/admin/ehr-integrations/new"
                  element={
                    <RoleGuard roles={['admin']}>
                      <EhrOnboardingWizard />
                    </RoleGuard>
                  }
                />

                <Route
                  path="/admin/ehr-integrations"
                  element={
                    <RoleGuard roles={['admin']}>
                      <EhrDashboardPage />
                    </RoleGuard>
                  }
                />

                {/* System log viewer — admin only */}
                <Route
                  path="/admin/logs"
                  element={
                    <RoleGuard roles={['admin']}>
                      <AdminLogsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/admin/events"
                  element={
                    <RoleGuard roles={['admin', 'customer_admin']}>
                      <OperationalEventsPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/admin/audit"
                  element={
                    <RoleGuard roles={['admin', 'customer_admin']}>
                      <AuditTrailPage />
                    </RoleGuard>
                  }
                />

                {/* Cross-org meta analytics — medflow_admin only */}
                <Route
                  path="/admin/meta-analytics"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <MetaAnalyticsPage />
                    </RoleGuard>
                  }
                />

                {/* Embedded Superset reports — medflow_admin only */}
                <Route
                  path="/admin/reports"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <ReportsPage />
                    </RoleGuard>
                  }
                />

                {/* Remote access portal — customer_admin + medflow_admin, gated by remote_access feature */}
                <Route
                  path="/admin/remote-access"
                  element={
                    <RoleGuard roles={['customer_admin', 'medflow_admin']}>
                      <RemoteAccessPage />
                    </RoleGuard>
                  }
                />

                {/* Container status — customer_admin + medflow_admin, no feature flag, tunnel-independent */}
                <Route
                  path="/admin/containers"
                  element={
                    <RoleGuard roles={['customer_admin', 'medflow_admin']}>
                      <ContainerStatusPage />
                    </RoleGuard>
                  }
                />
                {/* §4.8.4 Container shell — medflow_admin only, requires tunnel */}
                <Route
                  path="/admin/containers/shell"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <ContainerShellPage />
                    </RoleGuard>
                  }
                />
                {/* §7.5.3 Scheduled jobs — admin+ with nav surface on the admin page list. */}
                <Route
                  path="/admin/scheduled-jobs"
                  element={
                    <RoleGuard roles={['admin', 'customer_admin']}>
                      <ScheduledJobsPage />
                    </RoleGuard>
                  }
                />

                {/* §7.6.5 Archive viewer — admin | customer_admin | medflow_admin */}
                <Route
                  path="/admin/archive"
                  element={
                    <RoleGuard roles={['admin', 'customer_admin']}>
                      <ArchivePage />
                    </RoleGuard>
                  }
                />

                {/* Analytics sharing opt-out — customer_admin, data_sharing_opt_out license feature */}
                <Route
                  path="/admin/analytics-sharing"
                  element={
                    <RoleGuard roles={['customer_admin']}>
                      <AnalyticsSharingPage />
                    </RoleGuard>
                  }
                />

                {/* Off-site backup config — customer_admin, offsite_backup license feature */}
                <Route
                  path="/admin/offsite-backup"
                  element={
                    <RoleGuard roles={['customer_admin']}>
                      <OffsiteBackupPage />
                    </RoleGuard>
                  }
                />

                <Route
                  path="/admin/provisioning-config"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <ProvisioningConfigPage />
                    </RoleGuard>
                  }
                />

                <Route
                  path="/admin/rate-limit"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <RateLimitSettingsPage />
                    </RoleGuard>
                  }
                />

                {/* §7.6.21 Fax config + import logs — medflow_admin only */}
                <Route
                  path="/admin/fax-config"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FaxConfigPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/admin/fax-import-logs/:configId?"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <FaxImportLogsPage />
                    </RoleGuard>
                  }
                />
                {/* §7.7.2 Support access report — edge admin only */}
                <Route
                  path="/admin/support-access-report"
                  element={
                    <RoleGuard roles={['admin']}>
                      <SupportAccessReportPage />
                    </RoleGuard>
                  }
                />

                {/* §7.7.4 Staff access management — edge admin only */}
                <Route
                  path="/admin/medflow-staff"
                  element={
                    <RoleGuard roles={['admin']}>
                      <MedflowStaffAccessPage />
                    </RoleGuard>
                  }
                />

                {/* §7.7.3 Staff grants — medflow_admin (full CRUD) + customer_admin (read/revoke own org) */}
                <Route
                  path="/fleet/staff-grants"
                  element={
                    <RoleGuard roles={['medflow_admin', 'customer_admin']}>
                      <StaffGrantsPage />
                    </RoleGuard>
                  }
                />
                {/* §7.7.3 My active edges — self-view for medflow_admin */}
                <Route
                  path="/fleet/my-edges"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <MyActiveEdgesPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/provision"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <ProvisionWizardPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/provisioning"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <DeviceProvisioningPage />
                    </RoleGuard>
                  }
                />
                <Route
                  path="/fleet/udm-rollouts"
                  element={
                    <RoleGuard roles={['medflow_admin']}>
                      <UdmRolloutsPage />
                    </RoleGuard>
                  }
                />

                {/* Legacy routes redirect to settings tabs */}
                <Route path="/users" element={<Navigate to="/settings/users" replace />} />
                <Route path="/users/:id" element={<Navigate to="/settings/users" replace />} />
                <Route path="/test-types" element={<Navigate to="/settings/test-types" replace />} />
                <Route path="/providers" element={<Navigate to="/settings/providers" replace />} />
                <Route path="/org-settings" element={<Navigate to="/settings/org" replace />} />
                <Route path="/ehr-configs" element={<Navigate to="/settings/ehr" replace />} />
                <Route path="/ehr-configs/:id" element={<Navigate to="/settings/ehr" replace />} />
                <Route path="/system" element={<Navigate to="/settings/system" replace />} />
              </Route>
            </Routes>
          </PreferencesProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
